import React, {
  useCallback, useLayoutEffect, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4langPtBr from '@amcharts/amcharts4/lang/pt_BR';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

import * as S from './styles';

am4core.options.commercialLicense = true;
am4core.useTheme(am4themesAnimated);

const FILTER_TEXT = {
  StartDate: 'Início',
  TwelveMonths: '12M',
  TwentyFourMonths: '24M',
  ThirtySixMonths: '36M',
};

function ProfitabilityChart({ onFetchProfitabilityChart, data }) {
  const chartRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState(() => {
    if (data.dateFilterOptions.includes('TwelveMonths')) {
      return 'TwelveMonths';
    }
    return 'StartDate';
  });

  useLayoutEffect(() => {
    const chart = am4core.create('chartdiv', am4charts.XYChart);

    chart.data = data.data.map(item => ({
      ...item,
      date: new Intl.DateTimeFormat('pt-BR').format(new Date(item.date)),
    }));

    const xAxes = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxes.dataFields.category = 'date';
    xAxes.fontSize = 12;
    xAxes.tooltipText = '{date}';

    const yAxes = chart.yAxes.push(new am4charts.ValueAxis());
    yAxes.tooltip.disabled = true;

    const series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.categoryX = 'date';
    series.dataFields.valueY = 'acumulatedProfitabilityFund';
    series.strokeWidth = 1;
    series.minBulletDistance = 10;
    series.tooltipText = '{valueY.value}';
    series.name = data.fundName;

    const series2 = chart.series.push(new am4charts.LineSeries());
    series2.dataFields.categoryX = 'date';
    series2.dataFields.valueY = 'acumulatedProfitabilityBenchmark';
    series2.strokeWidth = 1;
    series2.minBulletDistance = 10;
    series2.tooltipText = '{valueY.value}';
    series2.fill = am4core.color('#999999');
    series2.stroke = am4core.color('#999999');
    series2.name = data.benchmarkName;

    chart.cursor = new am4charts.XYCursor();
    chart.legend = new am4charts.Legend();
    chart.language.locale = am4langPtBr;
    chart.numberFormatter.numberFormat = "#.##'%'";
    chart.legend.useDefaultMarker = true;
    chart.legend.markers.template.width = 40;
    chart.legend.markers.template.height = 8;

    chart.language.locale = am4langPtBr;
    chartRef.current = chart;

    return () => {
      chart.dispose();
    };
  }, [data]);

  const handleFilter = useCallback(async (filter) => {
    try {
      await onFetchProfitabilityChart(filter);
      setSelectedFilter(filter);
    } catch (error) {
      // supress error
    }
  }, []);

  return (
    <>
      <S.FlexAlign>
        <S.Title>Gráfico de rentabilidade</S.Title>
        <S.FilterContainer>
          {data.dateFilterOptions.map((filter) => {
            const isActive = selectedFilter === filter;
            return (
              <S.FilterButton
                key={filter}
                isActive={isActive}
                onClick={() => !isActive && handleFilter(filter)}
              >
                {FILTER_TEXT[filter]}
              </S.FilterButton>
            );
          })}
        </S.FilterContainer>
      </S.FlexAlign>
      <div id="chartdiv" style={{ width: '100%', height: '500px' }} />
    </>
  );
}

ProfitabilityChart.propTypes = {
  onFetchProfitabilityChart: PropTypes.func.isRequired,
  data: PropTypes.shape({
    benchmarkName: PropTypes.string,
    fundName: PropTypes.string,
    hasIndexer: PropTypes.bool,
    hasAtLeastSixMonthOld: PropTypes.bool,
    dateFilterOptions: PropTypes.arrayOf(
      PropTypes.oneOf([
        'StartDate',
        'TwelveMonths',
        'TwentyFourMonths',
        'ThirtySixMonths',
      ]),
    ),
    data: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        acumulatedProfitabilityBenchmark: PropTypes.number,
        acumulatedProfitabilityFund: PropTypes.number,
      }),
    ),
  }).isRequired,
};

export default ProfitabilityChart;
